import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MAINTENANCE_MESSAGE, isMaintenanceStatus, toFriendlyErrorMessage } from "../../../utils/network";

const serverUrl = process.env.REACT_APP_SERVER_HOST;
const TOKEN_KEY = "auth_token";

/* ─── helpers ────────────────────────────────────────────────────── */
const getAuthHeaders = () => {
  const token = sessionStorage.getItem(TOKEN_KEY);
  if (!token) return null;
  return { Authorization: `Bearer ${token}` };
};

const normalizeTestDto = (item) => {
  if (!item || typeof item !== "object") return null;

  const testId = Number(item.testId);
  if (!Number.isFinite(testId)) return null;

  return {
    testId,
    subject: typeof item.subject === "string" ? item.subject : "",
    type: typeof item.type === "string" ? item.type : `Test ${testId}`,
    description: typeof item.description === "string" ? item.description : "",
  };
};

/* ─── subject card config (fallback for unknown subjects) ─────────── */
const SUBJECT_CONFIG = {
  maths: { icon: "🔢", label: "Maths",   card: "#f0f4ff", border: "#c7d7fb", heading: "#1a56db", btn: "#1a56db", btnHover: "#1344b8" },
  math:  { icon: "🔢", label: "Maths",   card: "#f0f4ff", border: "#c7d7fb", heading: "#1a56db", btn: "#1a56db", btnHover: "#1344b8" },
  english: { icon: "📖", label: "English", card: "#f0fff6", border: "#9be7bc", heading: "#16794a", btn: "#16794a", btnHover: "#0e5233" },
};

const getSubjectCfg = (subject) =>
  SUBJECT_CONFIG[(subject || "").toLowerCase()] || {
    icon: "📚",
    label: subject,
    card: "#fef9f0",
    border: "#fcd9a0",
    heading: "#b45309",
    btn: "#b45309",
    btnHover: "#92400e",
  };

/* ─── One card per subject with all its test buttons ─────────────── */
const SubjectSection = ({ subject, tests, onTestClick }) => {
  const cfg = getSubjectCfg(subject);
  return (
    <div
      style={{
        background: cfg.card,
        border: `1px solid ${cfg.border}`,
        borderRadius: "12px",
        padding: "24px 28px",
        marginBottom: "24px",
      }}
    >
      <h2 style={{ margin: "0 0 18px", color: cfg.heading, fontSize: "20px", fontWeight: 700 }}>
        {cfg.icon} {cfg.label}
      </h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px" }}>
        {tests.map((test) => (
          <div
            key={test.testId}
            style={{
              background: "rgba(255,255,255,0.6)",
              border: `1px solid ${cfg.border}`,
              borderRadius: "6px",
              padding: "14px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <button
              onClick={() => onTestClick(test)}
              title={test.description || ""}
              style={{
                width: "100%",
                padding: "10px 18px",
                background: cfg.btn,
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: 600,
                letterSpacing: "0.3px",
                transition: "background 0.15s, transform 0.1s",
                marginBottom: test.description ? "10px" : 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = cfg.btnHover;
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = cfg.btn;
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              {test.type}
            </button>
            {test.description && (
              <div style={{ fontSize: "13px", lineHeight: 1.5, color: "#4b5563" }}>
                {test.description}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Main page ──────────────────────────────────────────────────── */
const HomePage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const successResult = state?.successResult || null;   // SubmitMockTestResponse
  const shouldSoftHandleAuthFailure = Boolean(successResult);

  const [tests, setTests]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [modalData, setModalData] = useState({ totalQuestions: 0, loading: false });

  useEffect(() => {
     const fetchTests = async () => {
       const headers = getAuthHeaders();
       if (!headers) {
         navigate("/login");
         return;
       }

       try {
         const res = await fetch(`${serverUrl}/app/tests`, { headers });
         if (res.status === 401) {
            if (shouldSoftHandleAuthFailure) {
              setError("Test submitted successfully, but your session has expired. Please login again.");
              setLoading(false);
              return;
            }
            sessionStorage.removeItem(TOKEN_KEY);
            navigate("/login");
           return;
        }
        if (isMaintenanceStatus(res.status)) {
          throw new Error(MAINTENANCE_MESSAGE);
        }
        if (!res.ok) throw new Error(`Failed to load tests (${res.status})`);
        const data = await res.json();
        const normalizedTests = Array.isArray(data)
          ? data.map(normalizeTestDto).filter(Boolean)
          : [];

        if (!Array.isArray(data)) {
          throw new Error("Unexpected tests response from server");
        }

        setTests(normalizedTests);
      } catch (err) {
        setError(toFriendlyErrorMessage(err, "Unable to load tests right now. Please try again."));
      } finally {
        setLoading(false);
      }
    };
    fetchTests();
  }, [navigate, shouldSoftHandleAuthFailure]);

  /* Group MockTestDto list by subject, ordered Maths → English → rest */
  const SUBJECT_ORDER = ["maths", "math", "english"];
  const grouped = tests.reduce((acc, t) => {
    const key = (t.subject || "other").toLowerCase();
    (acc[key] = acc[key] || []).push(t);
    return acc;
  }, {});
  const subjectKeys = [
    ...SUBJECT_ORDER.filter((k) => grouped[k]),
    ...Object.keys(grouped).filter((k) => !SUBJECT_ORDER.includes(k)),
  ];

   const handleTestClick = async (test) => {
     setSelectedTest(test);
     setModalData({ totalQuestions: 0, loading: true });
     setShowModal(true);

     try {
       const headers = getAuthHeaders();
       if (!headers) {
         navigate("/login");
         return;
       }

       const res = await fetch(`${serverUrl}/app/tests/${test.testId}/questions`, { headers });
       if (res.status === 401) {
         sessionStorage.removeItem(TOKEN_KEY);
         navigate("/login");
         return;
       }
       if (isMaintenanceStatus(res.status)) {
         throw new Error(MAINTENANCE_MESSAGE);
       }
       if (!res.ok) throw new Error(`Failed to load questions (${res.status})`);
       const wrapper = await res.json();
       if (!wrapper || typeof wrapper !== 'object') throw new Error("Unexpected questions response");

       const { durationMinutes, questions } = wrapper;
       if (!Array.isArray(questions)) throw new Error("Unexpected questions response");

       const totalQuestions = questions.reduce((sum, group) => sum + (group.questions ? group.questions.length : 0), 0);
       setModalData({ totalQuestions, durationMinutes, loading: false });
     } catch (err) {
       setModalData({ totalQuestions: 0, durationMinutes: 0, loading: false });
       setError(toFriendlyErrorMessage(err, "Unable to load test details."));
     }
   };

   const handleLogout = () => {
     navigate("/logout");
   };

   const handleModalCancel = () => {
     setShowModal(false);
     setSelectedTest(null);
     setModalData({ totalQuestions: 0, durationMinutes: 0, loading: false });
   };

   const handleModalOK = () => {
     if (!selectedTest) return;
     const totalTime = modalData.durationMinutes || 30; // Use API duration or fallback to 30
     navigate(`/tests/${selectedTest.testId}`, {
       state: { topicName: selectedTest.type, subject: selectedTest.subject, totalTime },
     });
   };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f7ff 0%, #eef9f2 100%)",
        padding: "0 16px 40px",
        fontFamily: "Arial, sans-serif",
      }}
    >
      {/* ── Top bar ───────────────────────────────────────────────── */}
      <div
        style={{
          maxWidth: "820px",
          margin: "0 auto",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "24px 0 32px",
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: "26px", color: "#1a1a2e", fontWeight: 800 }}>
            📚 Learning Hub
          </h1>
          <p style={{ margin: "4px 0 0", color: "#666", fontSize: "14px" }}>
            Choose a test to get started
          </p>
        </div>
        <button
          onClick={handleLogout}
          style={{
            padding: "9px 20px",
            background: "#dc3545",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          Logout
        </button>
      </div>

      <div style={{ maxWidth: "820px", margin: "0 auto" }}>
        {/* ── Error ────────────────────────────────────────────────── */}
        {error && (
          <div
            style={{
              padding: "14px 20px",
              background: "#fee2e2",
              color: "#991b1b",
              borderRadius: "8px",
              marginBottom: "24px",
              border: "1px solid #fca5a5",
            }}
          >
            ⚠️ {error}
          </div>
        )}

        {/* ── Content ──────────────────────────────────────────────── */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#888" }}>
            Loading tests…
          </div>
        ) : tests.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#aaa" }}>
            No tests available.
          </div>
        ) : (
          subjectKeys.map((key) => (
            <SubjectSection
              key={key}
              subject={key}
              tests={grouped[key]}
              onTestClick={handleTestClick}
            />
          ))
        )}
      </div>

      {/* ── Modal ─────────────────────────────────────────────────── */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "30px",
              borderRadius: "12px",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
            }}
          >
            <h3 style={{ margin: "0 0 20px", color: "#1f2937", fontSize: "20px" }}>
              Confirm Test Start
            </h3>
            {modalData.loading ? (
              <div style={{ textAlign: "center", color: "#666" }}>Loading details...</div>
            ) : (
              <div style={{ marginBottom: "20px" }}>
                <p><strong>Test Name:</strong> {selectedTest?.type}</p>
                <p><strong>Total Questions:</strong> {modalData.totalQuestions}</p>
                <p><strong>Total Time:</strong> {modalData.durationMinutes || 30} minutes</p>
              </div>
            )}
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                onClick={handleModalCancel}
                style={{
                  padding: "10px 20px",
                  background: "#6b7280",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleModalOK}
                disabled={modalData.loading}
                style={{
                  padding: "10px 20px",
                  background: "#007bff",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: modalData.loading ? "not-allowed" : "pointer",
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;

